import { useState, useRef, useEffect } from 'react';
import { 
  Image,
  Link as LinkIcon,
  Paperclip,
  X,
  Send,
  Activity,
  Plus,
  Smile,
  Code
} from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from '@/contexts/AuthContext';
import { createPost, uploadImage } from '@/lib/supabase';
import { Post as SupabasePost, PostImage } from '@/lib/supabase';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import logger from '@/lib/logger';
import EmojiPicker from "@/components/EmojiPicker";
import ToolPicker from "@/components/ToolPicker";
import SelectedTools from "@/components/SelectedTools";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";

const CreatePost = ({ onPostCreated }: { onPostCreated: (post: any) => void }) => {
  const { user, profile } = useAuth();
  const [content, setContent] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [linkUrl, setLinkUrl] = useState('');
  const [placeholderText, setPlaceholderText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkDialogInput, setLinkDialogInput] = useState('');
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const fullPlaceholderText = "What's on your mind?";
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openForCollaboration, setOpenForCollaboration] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  useEffect(() => {
    // Using refs for values that should persist between renders
    const animationState = {
      currentIndex: 0,
      isDeleting: false,
      typingTimer: null as ReturnType<typeof setTimeout> | null,
      pauseTimer: null as ReturnType<typeof setTimeout> | null
    };
    
    const typeText = () => {
      // If we're deleting, remove a character
      if (animationState.isDeleting) {
        if (animationState.currentIndex > 0) {
          animationState.currentIndex--;
          setPlaceholderText(fullPlaceholderText.substring(0, animationState.currentIndex));
          animationState.typingTimer = setTimeout(typeText, 50);
        } else {
          // When we've deleted everything, pause before typing again
          animationState.isDeleting = false;
          animationState.pauseTimer = setTimeout(typeText, 1000);
        }
      } 
      // If we're typing, add a character
      else {
        if (animationState.currentIndex < fullPlaceholderText.length) {
          animationState.currentIndex++;
          setPlaceholderText(fullPlaceholderText.substring(0, animationState.currentIndex));
          // Random timing between 100-150ms for more natural typing
          animationState.typingTimer = setTimeout(typeText, 100 + Math.random() * 50);
        } else {
          // When we've typed everything, pause before deleting
          animationState.pauseTimer = setTimeout(() => {
            animationState.isDeleting = true;
            typeText();
          }, 2000);
        }
      }
    };
    
    // Start the animation
    typeText();
    
    // Clean up all timers when component unmounts
    return () => {
      if (animationState.typingTimer) clearTimeout(animationState.typingTimer);
      if (animationState.pauseTimer) clearTimeout(animationState.pauseTimer);
    };
  }, [fullPlaceholderText]);
  
  useEffect(() => {
    // This is where the click outside handler was attached
    // Now we don't need to listen for clicks outside anymore since the tool selector is gone
    
    // For cleanup, just return an empty function
    return () => {};
  }, []);

  useEffect(() => {
    return () => {
      imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [imagePreviews]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newUploadedImages: File[] = [];
    const newImagePreviews: string[] = [];
    
    Array.from(files).forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} is larger than 5MB`,
          variant: "destructive",
        });
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not an image file`,
          variant: "destructive",
        });
        return;
      }

      newUploadedImages.push(file);
      const imageUrl = URL.createObjectURL(file);
      newImagePreviews.push(imageUrl);
    });

    setUploadedImages(prev => [...prev, ...newUploadedImages]);
    setImagePreviews(prev => [...prev, ...newImagePreviews]);
  };

  const handleOpenFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleAddLinkUrl = (url: string) => {
    if (url && url.trim()) {
      setLinkUrl(url.trim());
      toast({
        title: "Link added",
        description: `Added link: ${url.trim()}`,
      });
    }
  };

  const handleLinkDialogSubmit = () => {
    if (linkDialogInput.trim()) {
      handleAddLinkUrl(linkDialogInput.trim());
      setLinkDialogInput('');
      setShowLinkDialog(false);
    }
  };

  const handleClearImage = (index: number, isUrl: boolean) => {
    if (isUrl) {
      setImageUrls(prev => prev.filter((_, i) => i !== index));
    } else {
      URL.revokeObjectURL(imagePreviews[index]);
      
      setImagePreviews(prev => prev.filter((_, i) => i !== index));
      setUploadedImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleClearAllImages = () => {
    imagePreviews.forEach(preview => URL.revokeObjectURL(preview));
    
    setUploadedImages([]);
    setImagePreviews([]);
    setImageUrls([]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim() || !user?.id) return;
    
    setIsSubmitting(true);
    
    try {
      // Create a timeout promise that will reject after 30 seconds
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Post submission timed out after 30 seconds')), 30000);
      });
      
      // Process uploaded images (currently in memory)
      logger.info(`Starting to upload ${uploadedImages.length} images to Supabase Storage...`, {
        module: 'CreatePost'
      });
      
      if (uploadedImages.length > 0) {
        toast({
          title: "Uploading images...",
          description: `Uploading ${uploadedImages.length} image${uploadedImages.length > 1 ? 's' : ''}`,
        });
      }

      // Process all uploaded images with timeouts for each
      const uploadedImageUrls = [];
      for (let i = 0; i < uploadedImages.length; i++) {
        const file = uploadedImages[i];
        logger.info(`Uploading image ${i+1}/${uploadedImages.length}: ${file.name} (${file.size} bytes, type: ${file.type})...`, {
          module: 'CreatePost'
        });
        
        // Validate image file
        if (!file.type.startsWith('image/')) {
          logger.error(`File ${file.name} is not an image (type: ${file.type})`, {
            module: 'CreatePost'
          });
          // Continue with the next file
          continue;
        }
        
        // Limit size to avoid database issues
        const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
        if (file.size > MAX_FILE_SIZE) {
          logger.error(`File ${file.name} is too large (${file.size} bytes)`, {
            module: 'CreatePost'
          });
          toast({
            title: "File Too Large",
            description: `${file.name} exceeds the 5MB size limit.`,
            variant: "destructive"
          });
          continue;
        }
        
        try {
          // Upload image to Supabase Storage with timeout
          logger.info(`Uploading file ${file.name} to storage...`, {
            module: 'CreatePost'
          });
          
          // Create a single-image upload promise with timeout and pass user ID
          const uploadPromise = uploadImage(file, "post-images", user.id);
          const imageUploadWithTimeout = Promise.race([
            uploadPromise,
            new Promise((_, reject) => setTimeout(() => reject(new Error(`Upload timed out for ${file.name}`)), 15000))
          ]);
          
          const imageUrl = await imageUploadWithTimeout;
          
          if (imageUrl) {
            logger.info(`Successfully uploaded image to: ${imageUrl} (type: ${typeof imageUrl})`, {
              module: 'CreatePost'
            });
            
            // Ensure the URL is properly formatted
            if (typeof imageUrl === 'string') {
              // Format URL for consistent storage
              let formattedUrl = imageUrl.trim();
              // If it's just a path without a domain, ensure it's stored that way
              if (formattedUrl.startsWith('https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/')) {
                // Extract just the bucket/path part
                formattedUrl = formattedUrl.replace('https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/', '');
              }
              uploadedImageUrls.push(formattedUrl);
            } else {
              uploadedImageUrls.push(String(imageUrl));
            }
          } else {
            logger.error(`Failed to upload image: ${file.name} - no URL returned`, {
              module: 'CreatePost'
            });
            toast({
              title: "Image Upload Error",
              description: `Failed to upload ${file.name}.`,
              variant: "destructive"
            });
          }
        } catch (error) {
          logger.error(`Error uploading image ${file.name}`, {
            module: 'CreatePost',
            data: error
          });
          toast({
            title: "Image Upload Error",
            description: `Error uploading ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`,
            variant: "destructive"
          });
        }
      }

      // Combine with any external URLs
      const finalImageUrls = [...uploadedImageUrls, ...imageUrls];
      logger.info(`Total images to attach to post: ${finalImageUrls.length}`, {
        module: 'CreatePost',
        data: finalImageUrls
      });

      // Format image data for createPost with validation
      const imageData = finalImageUrls
        .filter(url => !!url && typeof url === 'string')
        .map(url => ({ 
          url: url.trim(),
          user_id: user.id // Include user_id with each image to support RLS
        }));

      logger.info('Final image data for post:', {
        module: 'CreatePost',
        data: imageData
      });

      // Create the post in Supabase with timeout
      logger.info(`Creating post with ${imageData.length} images...`, {
        module: 'CreatePost'
      });
      
      // Race the post creation against the timeout
      const createPostPromise = createPost({
        user_id: user.id,
        content,
        link_url: linkUrl || null,
        open_for_collaboration: openForCollaboration,
        images: imageData,
        tools: selectedTools,
        tags: [], // We could extract hashtags from content in a future version
      });
      
      const savedPost = await Promise.race([createPostPromise, timeoutPromise]);
      
      if (savedPost) {
        logger.info('Post created successfully:', {
          module: 'CreatePost',
          data: savedPost
        });
        
        // Check if the post has images properly associated
        const supaPost = savedPost as unknown as {
          id: string;
          content: string;
          user_id: string;
          created_at: string;
          updated_at?: string;
          is_published?: boolean;
          likes_count: number;
          upvotes_count: number;
          comments_count: number;
          shares_count?: number;
          link_url?: string | null;
          open_for_collaboration?: boolean;
          profiles?: {
            display_name?: string;
            username?: string;
            avatar_url?: string;
            level?: number;
            experience?: number;
          };
          post_images?: PostImage[];
        };
        
        // Log detailed information about the returned post and its images
        logger.info('Post images returned from database:', {
          module: 'CreatePost',
          data: {
            hasPostImages: !!supaPost.post_images,
            postImagesLength: supaPost.post_images?.length || 0,
            postImagesData: supaPost.post_images
          }
        });
        
        toast({
          title: "Post created",
          description: "Your post has been published!",
        });
        
        // Fetch the complete post with images to return
        const { data: completePost, error: fetchError } = await supabase
          .from("posts")
          .select(`
            *,
            profiles:user_id(id, username, display_name, avatar_url, level, experience),
            post_images(user_id, image_url, display_order),
            post_tools(tool)
          `)
          .eq("id", supaPost.id)
          .single();

        if (fetchError) {
          logger.error(`Error fetching complete post data:`, {
            module: 'CreatePost',
            data: fetchError
          });
          // Return the original post data if we can't fetch the complete post
          throw new Error(`Failed to fetch complete post: ${fetchError.message}`);
        }

        logger.info(`Retrieved complete post with images:`, {
          module: 'CreatePost',
          data: completePost
        });
        
        // Format the post to match PostProps and pass to parent
        const formattedPost = {
          id: supaPost.id,
          content: supaPost.content,
          images: supaPost.post_images?.map(img => {
            // Ensure the image URL is properly formatted for display
            let imageUrl = img.image_url;
            if (!imageUrl) return null;
            
            // Clean up the URL
            imageUrl = imageUrl.trim();
            
            // If it's a bucket path without http://, add the full Supabase URL
            if (!imageUrl.startsWith('http') && imageUrl.includes('/')) {
              imageUrl = `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${imageUrl}`;
            }
            
            return {
              image_url: imageUrl
            };
          }).filter(img => img !== null) || [],
          link_url: supaPost.link_url,
          user: {
            id: supaPost.user_id,
            name: supaPost.profiles?.display_name || 'User',
            username: `@${supaPost.profiles?.username || 'user'}`,
            avatar_url: supaPost.profiles?.avatar_url || '/placeholder.svg',
            level: supaPost.profiles?.level || 1,
            experience: supaPost.profiles?.experience || 0,
          },
          created_at: supaPost.created_at,
          likes: supaPost.likes_count || 0,
          upvotes: supaPost.upvotes_count || 0,
          comments: supaPost.comments_count || 0,
          shares: supaPost.shares_count || 0,
          openForCollaboration: supaPost.open_for_collaboration,
          tools: completePost.post_tools?.map(pt => pt.tool) || selectedTools, // Use tools from database or fallback to selected tools
          tags: [],
        };
        
        // Final check on formatted post before passing to UI
        logger.info('Formatted post for UI:', {
          module: 'CreatePost',
          data: {
            id: formattedPost.id,
            content: formattedPost.content,
            user: formattedPost.user,
            imageCount: formattedPost.images?.length || 0,
            toolsCount: formattedPost.tools?.length || 0,
            tools: formattedPost.tools
          }
        });
        
        // Update the UI with the new post
        onPostCreated(formattedPost);
        
        // Reset form
        setContent('');
        setImageUrls([]);
        setLinkUrl('');
        setSelectedTools([]);
        setOpenForCollaboration(false);
        handleClearAllImages();
      }
    } catch (error) {
      logger.error('Error creating post:', {
        module: 'CreatePost',
        data: error
      });
      
      // Specific error handling for timeouts
      const errorMessage = error instanceof Error 
        ? (error.message.includes('timed out') 
            ? 'The operation timed out. Please try again with a smaller image or check your connection.' 
            : error.message)
        : 'There was a problem publishing your post. Please try again.';
      
      toast({
        title: "Error creating post",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const hasImages = imagePreviews.length > 0;
  
  // Add emoji to content at cursor position
  const handleEmojiSelect = (emoji: string) => {
    // Get cursor position
    const textarea = textareaRef.current;
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // Insert emoji at cursor position
    const newContent = content.substring(0, start) + emoji + content.substring(end);
    setContent(newContent);
    
    // Set cursor position after emoji
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + emoji.length;
      textarea.selectionEnd = start + emoji.length;
    }, 10);
  };
  
  // Toggle tool selection
  const toggleTool = (toolId: string) => {
    setSelectedTools(prevTools => 
      prevTools.includes(toolId)
        ? prevTools.filter(id => id !== toolId)
        : [...prevTools, toolId]
    );
  };
  
  return (
    <div className="vibebops-card mb-6">
      <form onSubmit={handleSubmit}>
        <div className="flex gap-3 mb-4">
          <Avatar className="h-10 w-10 border-2 border-vibebops-highlight">
            <AvatarImage src={profile?.avatar_url || "/placeholder.svg"} alt={profile?.display_name || "Your avatar"} />
            <AvatarFallback className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold">
              {profile?.display_name?.[0]?.toUpperCase() || "U"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={placeholderText}
              className="w-full bg-transparent border-0 focus:ring-0 resize-none text-vibebops-text placeholder:text-vibebops-textMuted placeholder:pl-2 pl-2"
              rows={3}
              ref={textareaRef}
            ></textarea>
            
            {/* Display selected tools */}
            <SelectedTools 
              selectedTools={selectedTools} 
              toggleTool={toggleTool} 
            />
            
            {hasImages && (
              <div className="mt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <h4 className="text-sm font-medium">Added Images</h4>
                  <button
                    type="button"
                    onClick={handleClearAllImages}
                    className="text-xs text-vibebops-textMuted hover:text-vibebops-text"
                  >
                    Clear all
                  </button>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {imagePreviews.map((preview, index) => (
                    <div key={`preview-${index}`} className="relative rounded-lg overflow-hidden">
                      <AspectRatio ratio={16 / 9}>
                        <img src={preview} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                      </AspectRatio>
                      <button 
                        type="button"
                        onClick={() => handleClearImage(index, false)}
                        className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  
                  {imageUrls.map((url, index) => (
                    <div key={`url-${index}`} className="relative rounded-lg overflow-hidden">
                      <AspectRatio ratio={16 / 9}>
                        <img src={url} alt={`Attached ${index + 1}`} className="w-full h-full object-cover" />
                      </AspectRatio>
                      <button 
                        type="button"
                        onClick={() => handleClearImage(index, true)}
                        className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={handleOpenFilePicker}
                    className="flex flex-col items-center justify-center bg-vibebops-highlight rounded-lg p-4 hover:bg-vibebops-highlight/70 transition-colors"
                  >
                    <Paperclip className="h-6 w-6 mb-1" />
                    <span className="text-xs">Upload Image</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="collaboration-mode"
              checked={openForCollaboration}
              onCheckedChange={(checked) => setOpenForCollaboration(!!checked)}
            />
            <Label 
              htmlFor="collaboration-mode" 
              className="text-vibebops-text cursor-pointer"
            >
              Open For Collaboration
            </Label>
          </div>
          
          {linkUrl && (
            <div className="flex items-center px-2 py-1 bg-vibebops-highlight/50 rounded-lg text-sm gap-2">
              <LinkIcon className="h-4 w-4" />
              <span className="truncate max-w-[200px]">{linkUrl}</span>
              <button
                type="button"
                onClick={() => setLinkUrl('')}
                className="text-vibebops-textMuted hover:text-vibebops-text"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleFileChange}
            />
            
            {!hasImages && (
              <button 
                type="button" 
                className="p-2 rounded-lg text-vibebops-textMuted hover:bg-vibebops-highlight"
                onClick={handleOpenFilePicker}
              >
                <Paperclip className="h-5 w-5" />
              </button>
            )}

            <Popover>
              <PopoverTrigger asChild>
                <button 
                  type="button" 
                  className="p-2 rounded-lg text-vibebops-textMuted hover:bg-vibebops-highlight"
                  aria-label="Add emoji"
                >
                  <Smile className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 border-vibebops-border shadow-lg" align="start" side="top" sideOffset={5}>
                <EmojiPicker onEmojiSelect={handleEmojiSelect} />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button 
                  type="button" 
                  className="p-2 rounded-lg text-vibebops-textMuted hover:bg-vibebops-highlight"
                  aria-label="Add tools & technologies"
                >
                  <Code className="h-5 w-5" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0 border-vibebops-border shadow-lg" align="start" side="top" sideOffset={5}>
                <ToolPicker 
                  selectedTools={selectedTools} 
                  onToolSelect={toggleTool} 
                />
              </PopoverContent>
            </Popover>

            <button 
              type="button" 
              className="p-2 rounded-lg text-vibebops-textMuted hover:bg-vibebops-highlight border-2 border-black relative"
              onClick={() => setShowLinkDialog(true)}
              aria-label="Add website or app link"
            >
              <LinkIcon className="h-5 w-5" />
              <span className="absolute -top-2 -right-2 text-xs bg-vibebops-accent text-white rounded-full px-1">🔗</span>
            </button>
          </div>
          
          <div className="flex justify-end">
            <Button 
              type="submit" 
              className="bg-vibebops-accent hover:bg-vibebops-accent/90 text-white"
              disabled={!content.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Add Bop
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
      
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="bg-vibebops-card border border-vibebops-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-vibebops-text text-xl">Add Link</DialogTitle>
            <DialogDescription className="text-vibebops-textMuted">
              Enter the URL of the website or app you want to share
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              value={linkDialogInput}
              onChange={(e) => setLinkDialogInput(e.target.value)}
              placeholder="https://example.com"
              className="col-span-3 bg-vibebops-highlight/30 border-vibebops-border text-vibebops-text"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLinkDialogSubmit();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button" 
              onClick={() => setShowLinkDialog(false)}
              variant="outline"
              className="border-vibebops-border text-vibebops-text"
            >
              Cancel
            </Button>
            <Button 
              type="button"
              onClick={handleLinkDialogSubmit} 
              className="bg-vibebops-accent text-white hover:bg-vibebops-accent/90"
              disabled={!linkDialogInput.trim()}
            >
              Add Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreatePost;
